import React, { useState, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { FloatLabel } from 'primereact/floatlabel';
import { InputText } from 'primereact/inputtext';
import { Country, State, City } from 'country-state-city';
import usCounties from '../data/usCounties.json';

const LocationDropdowns = ({ 
  formik, 
  showStreetName = false, 
  showStreetNumber = false,
  showCompassDirection = false,
  showPostStreetDirection = false,
  showUnitNumber = false,
  showZipCode = false
}) => {
  const [stateOptions, setStateOptions] = useState([]);
  const [countyOptions, setCountyOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [streetOptions, setStreetOptions] = useState([]);

  // Country list with United States first
  const countryList = Country.getAllCountries().map(country => ({
    label: country.name,
    value: country.isoCode
  })).sort((a, b) => {
    if (a.value === "US") return -1;
    if (b.value === "US") return 1;
    return a.label.localeCompare(b.label);
  });

  // Compass directions
  const compassDirections = [
    { label: "None", value: "None" },
    { label: "N", value: "N" },
    { label: "S", value: "S" },
    { label: "E", value: "E" },
    { label: "W", value: "W" },
    { label: "NE", value: "NE" },
    { label: "NW", value: "NW" },
    { label: "SE", value: "SE" },
    { label: "SW", value: "SW" },
  ];

  // Post street directions
  const postStreetDirections = [
    { label: "None", value: "None" },
    { label: "N", value: "N" },
    { label: "S", value: "S" },
    { label: "E", value: "E" },
    { label: "W", value: "W" },
    { label: "NE", value: "NE" },
    { label: "NW", value: "NW" },
    { label: "SE", value: "SE" },
    { label: "SW", value: "SW" },
  ];

  // Sample street names (you can expand this or connect to an API)
  const sampleStreetNames = [
    "Main Street", "Oak Avenue", "Pine Road", "Elm Street", "Maple Drive",
    "Cedar Lane", "Washington Boulevard", "Park Avenue", "Broadway",
    "First Street", "Second Avenue", "Third Road", "Fourth Drive",
    "Fifth Lane", "Sixth Boulevard", "Seventh Avenue", "Eighth Street",
    "Ninth Road", "Tenth Drive", "Lake View Drive", "River Road",
    "Mountain Avenue", "Valley Street", "Sunset Boulevard", "Sunrise Drive",
    "Ocean Avenue", "Beach Road", "Forest Lane", "Garden Street",
    "School Avenue", "Church Street", "Hospital Drive", "Library Lane"
  ];

  // NEW: normalize country to ISO code if a name was provided
  useEffect(() => {
    const cVal = formik.values.country;
    if (cVal && cVal.length > 2) {
      const match = Country.getAllCountries().find(c => c.name === cVal);
      if (match && match.isoCode !== cVal) {
        formik.setFieldValue('country', match.isoCode);
      }
    }
  }, [formik.values.country]);  

  // Set state options when country changes
  useEffect(() => {
    if (formik.values.country) {
      const states = State.getStatesOfCountry(formik.values.country);
      const opts = states.map(state => ({ label: state.name, value: state.isoCode }));
      setStateOptions(opts);
      // Clear state and county when country changes
      formik.setFieldValue('state', '');
      formik.setFieldValue('county', '');
    } else {
      setStateOptions([]);
    }
  }, [formik.values.country]);

  // NEW: normalize state to ISO code if a name was provided
  useEffect(() => {
    const countryIso = formik.values.country;
    const sVal = formik.values.state;
    if (!countryIso || !sVal) return;
    const states = State.getStatesOfCountry(countryIso);
    // if already isoCode, keep it; otherwise map name -> isoCode
    const isIso = states.some(s => s.isoCode === sVal);
    if (!isIso) {
      const byName = states.find(s => s.name === sVal);
      if (byName) formik.setFieldValue('state', byName.isoCode);
    }
  }, [formik.values.country, formik.values.state]);

  // Normalize city if it's an object (e.g., { name: "Miami" })
  useEffect(() => {
    const cv = formik.values.city;
    if (cv && typeof cv === "object") {
      if (cv.name && typeof cv.name === "string") {
        formik.setFieldValue("city", cv.name);
      } else {
        formik.setFieldValue("city", "");
      }
    }
  }, [formik.values.city, formik.setFieldValue]);

  useEffect(() => {
    // Set city options when state changes
    if (formik.values.country && formik.values.state) {
      const cities = City.getCitiesOfState(formik.values.country, formik.values.state);
      const opts = cities.map(city => ({ label: city.name, value: city.name }));

      // Try to normalize the current city to the exact option label
      const raw = formik.values.city;
      const current = typeof raw === "string" ? raw.trim() : raw;

      if (current) {
        const match = cities.find(
          c => c.name.toLowerCase() === String(current).toLowerCase()
        );
        if (match && match.name !== current) {
          // Normalize to exact canonical name
          formik.setFieldValue("city", match.name);
        } else if (!match) {
          // Inject current value so it still shows even if not in library
          opts.unshift({ label: String(current), value: String(current) });
        }
      }

      setCityOptions(opts);
    } else {
      setCityOptions([]);
    }
  }, [formik.values.state, formik.values.country, formik.values.city, formik.setFieldValue]);

  useEffect(() => {
    // Set street options (you can expand this with API calls)
    setStreetOptions(sampleStreetNames.map(street => ({ label: street, value: street })));
  }, []);

  const isUS = formik.values.country === "US";

  const showStateProvinceDropdown = formik.values.country;
  const stateProvinceLabel = formik.values.country === "CA" ? "Province" : "State";
  const cityLabel = "City";

  return (
    <div className="space-y-4">
      {/* Country Dropdown */}
      <div className="py-2 my-4">
        <FloatLabel>
          <Dropdown
            id="country"
            name="country"
            value={formik.values.country || ""}
            options={countryList}
            onChange={(e) => {
              formik.setFieldValue('country', e.value);
              formik.setFieldValue('state', '');
              formik.setFieldValue('county', '');
              formik.setFieldValue('city', '');
            }}
            className="w-full border border-gray-500 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-100"
          />
          <label htmlFor="country" className="text-sm">
            Country
          </label>
        </FloatLabel>
        {formik.touched.country && formik.errors.country && (
          <div className="text-[#BA1500] text-sm" aria-live="polite">
            {formik.errors.country}
          </div>
        )}
      </div>

      {/* State/Province Dropdown */}
      {showStateProvinceDropdown && (
        <div className="py-2 my-2">
          <FloatLabel>
            <Dropdown
              id="state"
              name="state"
              value={formik.values.state || ""}
              options={stateOptions}
              onChange={(e) => {
                formik.setFieldValue('state', e.value);
                formik.setFieldValue('county', ''); // Clear county when state changes
                formik.setFieldValue('city', ''); // Clear city when state changes
              }}
              className="w-full border border-gray-500 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-100"
            />
            <label htmlFor="state" className="text-sm">
              {stateProvinceLabel}
            </label>
          </FloatLabel>
          {formik.touched.state && formik.errors.state && (
            <div className="text-[#BA1500] text-sm" aria-live="polite">
              {formik.errors.state}
            </div>
          )}
        </div>
      )}

      {/* County Dropdown (only for US when state is selected) */}
      {isUS && formik.values.state && (
        <div className="py-2 my-2">
          <FloatLabel>
            <Dropdown
              id="county"
              name="county"
              value={formik.values.county || ""}
              options={usCounties[formik.values.state] || []}
              optionLabel="name"
              optionValue="name"
              onChange={(e) => formik.setFieldValue('county', e.value)}
              className="w-full border border-gray-500 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-100"
              filter
              showClear
              placeholder="Select County"
            />
            <label htmlFor="county" className="text-sm">
              County
            </label>
          </FloatLabel>
          {formik.touched.county && formik.errors.county && (
            <div className="text-[#BA1500] text-sm" aria-live="polite">
              {formik.errors.county}
            </div>
          )}
        </div>
      )}

      

      {/* Street Number */}
      {showStreetNumber && (
        <div className="py-2 my-2">
          <FloatLabel>
            <InputText
              id="street_number"
              name="street_number"
              type="number"
              className="w-full border border-gray-500 rounded-md placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-gray-100"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.street_number || ""}
            />
            <label htmlFor="street_number" className="text-sm">
              Street Number
            </label>
          </FloatLabel>
          {formik.touched.street_number && formik.errors.street_number && (
            <div className="text-[#BA1500] text-sm" aria-live="polite">
              {formik.errors.street_number}
            </div>
          )}
        </div>
      )}

      {/* Unit Number */}
      {showUnitNumber && (
        <div className="py-2 my-2">
          <FloatLabel>
            <InputText
              id="unit_number"
              name="unit_number"
              type="text"
              className="w-full border border-gray-500 rounded-md placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-gray-100"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.unit_number || ""}
            />
            <label htmlFor="unit_number" className="text-sm">
              Unit #
            </label>
          </FloatLabel>
          {formik.touched.unit_number && formik.errors.unit_number && (
            <div className="text-[#BA1500] text-sm" aria-live="polite">
              {formik.errors.unit_number}
            </div>
          )}
        </div>
      )}

      {/* Compass Direction */}
      {showCompassDirection && (
        <div className="py-2 my-2">
          <FloatLabel>
            <Dropdown
              id="compass_direction"
              name="compass_direction"
              value={formik.values.compass_direction || ""}
              options={compassDirections}
              onChange={(e) => formik.setFieldValue('compass_direction', e.value)}
              className="w-full border border-gray-500 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-100"
            />
            <label htmlFor="compass_direction" className="text-sm">
              Compass Direction
            </label>
          </FloatLabel>
          {formik.touched.compass_direction && formik.errors.compass_direction && (
            <div className="text-[#BA1500] text-sm" aria-live="polite">
              {formik.errors.compass_direction}
            </div>
          )}
        </div>
      )}

      {/* Street Name */}
      {showStreetName && (
        <div className="py-2 my-2">
          <FloatLabel>
            <Dropdown
              id="street_name"
              name="street_name"
              value={formik.values.street_name || ""}
              options={streetOptions}
              onChange={(e) => formik.setFieldValue('street_name', e.value)}
              className="w-full border border-gray-500 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-100"
              filter
              showClear
            />
            <label htmlFor="street_name" className="text-sm">
              Street Name
            </label>
          </FloatLabel>
          {formik.touched.street_name && formik.errors.street_name && (
            <div className="text-[#BA1500] text-sm" aria-live="polite">
              {formik.errors.street_name}
            </div>
          )}
        </div>
      )}

      {/* Post Street Direction */}
      {showPostStreetDirection && (
        <div className="py-2 my-2">
          <FloatLabel>
            <Dropdown
              id="post_street_direction"
              name="post_street_direction"
              value={formik.values.post_street_direction || ""}
              options={postStreetDirections}
              onChange={(e) => formik.setFieldValue('post_street_direction', e.value)}
              className="w-full border border-gray-500 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-100"
            />
            <label htmlFor="post_street_direction" className="text-sm">
              Post Street Direction
            </label>
          </FloatLabel>
          {formik.touched.post_street_direction && formik.errors.post_street_direction && (
            <div className="text-[#BA1500] text-sm" aria-live="polite">
              {formik.errors.post_street_direction}
            </div>
          )}
        </div>
      )}

      

      {/* City Dropdown - Show for all countries when state/province is selected */}
      {formik.values.state && (
        <div className="py-2 my-2">
          <FloatLabel>
            <Dropdown
              id="city"
              name="city"
              value={formik.values.city || ""}
              options={cityOptions}
              optionLabel="label"      // explicit to avoid mismatches
              optionValue="value"
              onChange={(e) => formik.setFieldValue('city', e.value)}
              className="w-full border border-gray-500 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-100"
              filter
              showClear
            />
            <label htmlFor="city" className="text-sm">
              City
            </label>
          </FloatLabel>
          {formik.touched.city && formik.errors.city && (
            <div className="text-[#BA1500] text-sm" aria-live="polite">
              {formik.errors.city}
            </div>
          )}
        </div>
      )}

      {/* Zip Code */}
      {showZipCode && (
        <div className="py-2 my-2">
          <FloatLabel>
            <InputText
              id="zip_code"
              name="zip_code"
              type="text"
              className="w-full border border-gray-500 rounded-md placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-gray-100"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.zip_code || ""}
            />
            <label htmlFor="zip_code" className="text-sm">
              Zip Code
            </label>
          </FloatLabel>
          {formik.touched.zip_code && formik.errors.zip_code && (
            <div className="text-[#BA1500] text-sm" aria-live="polite">
              {formik.errors.zip_code}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationDropdowns;
